import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playSound } from '../../lib/soundEffects';

const { FiShield, FiLock, FiClock, FiKey, FiCheck, FiRefreshCw } = FiIcons;

export default function TabVault({ theme, accent = '#10b981' }) {
  const { textColor, mutedText, inputBg, inputBorder, surfaceBg, cardBg } = theme;

  const [clipboardTimeout, setClipboardTimeout] = useState(() => {
    return parseInt(localStorage.getItem('memark_clipboard_timeout') || '30', 10);
  });
  const [pwdLength, setPwdLength] = useState(() => {
    return parseInt(localStorage.getItem('memark_default_pwd_len') || '20', 10);
  });
  const [pbkdf2Rounds, setPbkdf2Rounds] = useState(() => {
    return localStorage.getItem('memark_pbkdf2_rounds') || '100000';
  });

  const handleTimeoutChange = (val) => {
    setClipboardTimeout(val);
    localStorage.setItem('memark_clipboard_timeout', val.toString());
    playSound('click');
  };

  const handlePwdLengthChange = (val) => {
    setPwdLength(val);
    localStorage.setItem('memark_default_pwd_len', val.toString());
    playSound('click');
  };

  const handlePbkdf2Change = (val) => {
    setPbkdf2Rounds(val);
    localStorage.setItem('memark_pbkdf2_rounds', val);
    playSound('lock');
  };

  return (
    <div className="space-y-6">
      {/* Vault Status Header */}
      <div className="flex items-center justify-between p-4 rounded-2xl border"
        style={{ backgroundColor: surfaceBg, borderColor: inputBorder }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-[0_0_15px_rgba(16,185,129,0.2)]"
            style={{ backgroundColor: `${accent}20`, border: `1px solid ${accent}40` }}>
            <SafeIcon icon={FiShield} className="w-5 h-5" style={{ color: accent }} />
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: textColor }}>
              Zero-Knowledge Client-Side Vault
            </p>
            <p className="text-xs" style={{ color: mutedText }}>
              AES-GCM-256 + PBKDF2 Web Crypto Enclave
            </p>
          </div>
        </div>

        <span className="px-2.5 py-1 rounded-full text-[10px] font-mono font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
          ENC:v1 ACTIVE
        </span>
      </div>

      {/* Ephemeral Clipboard Timeout */}
      <div className="space-y-2">
        <div className="flex justify-between items-center text-xs">
          <span className="font-semibold" style={{ color: textColor }}>Ephemeral Clipboard Auto-Wipe Timer</span>
          <span className="font-mono text-[11px]" style={{ color: accent }}>{clipboardTimeout} seconds</span>
        </div>
        <p className="text-[11px]" style={{ color: mutedText }}>
          Copied passwords and credentials are automatically erased from your system buffer after this duration.
        </p>
        <div className="grid grid-cols-3 gap-2 pt-1">
          {[15, 30, 60].map((t) => (
            <button
              key={t}
              onClick={() => handleTimeoutChange(t)}
              className="py-2 rounded-xl border text-xs font-mono font-semibold transition-all"
              style={{
                backgroundColor: clipboardTimeout === t ? `${accent}20` : cardBg,
                borderColor: clipboardTimeout === t ? accent : inputBorder,
                color: clipboardTimeout === t ? textColor : mutedText,
              }}
            >
              {t} Seconds
            </button>
          ))}
        </div>
      </div>

      {/* Default Generated Password Length */}
      <div className="space-y-2 pt-2 border-t" style={{ borderColor: inputBorder }}>
        <div className="flex justify-between items-center text-xs">
          <span className="font-semibold" style={{ color: textColor }}>Default Strong Password Length</span>
          <span className="font-mono text-[11px]" style={{ color: accent }}>{pwdLength} Characters</span>
        </div>
        <div className="grid grid-cols-4 gap-2 pt-1">
          {[16, 20, 24, 32].map((l) => (
            <button
              key={l}
              onClick={() => handlePwdLengthChange(l)}
              className="py-2 rounded-xl border text-xs font-mono font-semibold transition-all"
              style={{
                backgroundColor: pwdLength === l ? `${accent}20` : cardBg,
                borderColor: pwdLength === l ? accent : inputBorder,
                color: pwdLength === l ? textColor : mutedText,
              }}
            >
              {l} Chars
            </button>
          ))}
        </div>
      </div>

      {/* PBKDF2 Iteration Rounds */}
      <div className="space-y-2 pt-2 border-t" style={{ borderColor: inputBorder }}>
        <div className="flex justify-between items-center text-xs">
          <span className="font-semibold" style={{ color: textColor }}>PBKDF2 Key Derivation Iterations</span>
          <span className="font-mono text-[11px]" style={{ color: accent }}>{parseInt(pbkdf2Rounds, 10).toLocaleString()} Rounds</span>
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1">
          {[
            { rounds: '100000', label: '100,000 Rounds', desc: 'Standard Military Spec (Fast)' },
            { rounds: '200000', label: '200,000 Rounds', desc: 'Extreme Paranoia (Ultra-Secure)' },
          ].map((item) => (
            <button
              key={item.rounds}
              onClick={() => handlePbkdf2Change(item.rounds)}
              className="p-3 rounded-xl border text-left transition-all"
              style={{
                backgroundColor: pbkdf2Rounds === item.rounds ? `${accent}15` : cardBg,
                borderColor: pbkdf2Rounds === item.rounds ? accent : inputBorder,
              }}
            >
              <div className="text-xs font-bold" style={{ color: pbkdf2Rounds === item.rounds ? textColor : mutedText }}>{item.label}</div>
              <p className="text-[10px]" style={{ color: mutedText }}>{item.desc}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
